import {ButtonLink} from '@/components/ui/button-link/ButtonLink'
import {Button} from '@/components/ui/button/Button'
import {Dialog, DialogBody, DialogDisclosure, DialogProvider} from '@/components/ui/dialog-atoms/DialogAtoms'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {PencilLineIcon} from '@/components/ui/icon/Icon'
import {Text} from '@/components/ui/text/Text'
import {Tooltip} from '@/components/ui/tooltip/Tooltip'
import {
    StyledProjectPlannedAccessesEditDialog,
    StyledProjectPlannedAccessesEditDialogFooter,
    StyledProjectPlannedAccessesEditDialogHeader,
    StyledProjectPlannedAccessesEditDialogHeading
} from '@/features/projects/components/project-planned-accesses-edit-button/style'
import {useFm22Window} from '@/features/projects/hooks/useFm22Window'
import {ProjectExtended} from '@/features/projects/types'
import {canEditFm22Role} from '@/features/projects/utils'
import {useAuthStore} from '@/features/auth/store/store'
import {useTranslation} from '@/translations/i18n'
import {useDialogContext} from '@ariakit/react'
import dayjs from 'dayjs'

export const ProjectPlannedAccessesEditButton = ({project}: {project: ProjectExtended}) => {
    const {t} = useTranslation()
    const authUser = useAuthStore(state => state.user)
    const {canEditFm22, editDeadline} = useFm22Window(project)

    if (!canEditFm22 || !canEditFm22Role(authUser?.roleName)) {
        return null
    }

    return (
        <DialogProvider>
            <Tooltip
                variant="light"
                content={
                    editDeadline
                        ? t('projects:fm22_edit_deadline', {date: editDeadline.format('DD/MM/YYYY')})
                        : t('projects:fm22_edit_until_siar_print')
                }
            >
                <DialogDisclosure
                    render={
                        <Button size="sm" variant="ghost">
                            <PencilLineIcon />
                            {t('commons:edit')}
                        </Button>
                    }
                />
            </Tooltip>
            <Dialog width="400px">
                <DialogContent project={project} />
            </Dialog>
        </DialogProvider>
    )
}

const DialogContent = ({project}: {project: ProjectExtended}) => {
    const {t} = useTranslation()
    const dialogContext = useDialogContext()

    return (
        <StyledProjectPlannedAccessesEditDialog>
            <StyledProjectPlannedAccessesEditDialogHeader>
                <StyledProjectPlannedAccessesEditDialogHeading>
                    {t('planned_accesses:edit_planned_accesses')}
                </StyledProjectPlannedAccessesEditDialogHeading>
                <Flexbox direction="column" gap={1}>
                    <Text size="textSm" palette="neutral" shade="950">
                        {t('projects:code_date', {
                            code: project.code,
                            date: dayjs(project.startDate).format('DD/MM/YYYY')
                        })}
                    </Text>
                    <Text size="textSm" palette="neutral" shade="500">
                        {t('commons:patient_fullname_value', {
                            fullName: project.patient.fullName
                        })}
                    </Text>
                </Flexbox>
            </StyledProjectPlannedAccessesEditDialogHeader>
            <DialogBody>
                <Text size="textSm" palette="neutral" shade="600">
                    {t('planned_accesses:edit_description')}
                </Text>
            </DialogBody>
            <StyledProjectPlannedAccessesEditDialogFooter>
                <Button size="sm" variant="secondary" onClick={() => dialogContext?.setOpen(false)}>
                    {t('commons:cancel')}
                </Button>
                <ButtonLink size="sm" variant="primaryNeutral" to="edit">
                    {t('commons:edit')}
                </ButtonLink>
            </StyledProjectPlannedAccessesEditDialogFooter>
        </StyledProjectPlannedAccessesEditDialog>
    )
}
